import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { MessageCircleQuestion } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useHelpChat } from "@/lib/helpChat";
import { startGuide, startTour } from "@/lib/tour";
import { HelpPanel } from "@/components/help/HelpPanel";
import { cn } from "@/lib/utils";

// cada passo leva a permissão do artigo (área ou "só administrador"), conferida de novo pelo GuidedTour antes de navegar
export const guiaParaTour = (a) => (a?.guia || []).map((p) => ({ route: p.rota, targets: [`[data-testid="${p.alvo}"]`], title: p.titulo, text: p.texto,
  clique: p.clique, area: a.area || null, admin: !!a.admin }));
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const newId = () => Math.random().toString(36).slice(2, 10);

const TAM = 56; // tamanho do botão (h-14)
const MARGEM = 8;
const lerPos = (k) => { try { const v = JSON.parse(localStorage.getItem(k) || "null"); return v && Number.isFinite(v.x) && Number.isFinite(v.y) ? v : null; } catch { return null; } };
// limite superior primeiro e inferior por último: mesmo numa tela menor que o botão, a posição nunca fica negativa
const prender = (p) => p && { x: Math.max(MARGEM, Math.min(p.x, window.innerWidth - TAM - MARGEM)), y: Math.max(MARGEM, Math.min(p.y, window.innerHeight - TAM - MARGEM)) };

// Botão de ajuda flutuante: clique abre; clique, segure e arraste para mudar de lugar (mouse ou dedo).
// A posição fica guardada neste aparelho e o botão nunca sai da tela (inclusive ao girar o celular ou redimensionar a janela).
function HelpFab({ variant, pathname, fresh, onClick, userId }) {
  const chave = `help_fab_pos_${variant}_${userId}`;
  const [pos, setPos] = useState(() => prender(lerPos(chave)));
  const arraste = useRef(null);
  useEffect(() => {
    const ajustar = () => setPos((p) => prender(p));
    window.addEventListener("resize", ajustar);
    return () => window.removeEventListener("resize", ajustar);
  }, []);
  const salvar = (p) => { try { if (p) localStorage.setItem(chave, JSON.stringify(p)); else localStorage.removeItem(chave); } catch { /* sem armazenamento: só não lembra */ } };
  const down = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const r = e.currentTarget.getBoundingClientRect();
    arraste.current = { id: e.pointerId, sx: e.clientX, sy: e.clientY, dx: e.clientX - r.left, dy: e.clientY - r.top, moveu: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const a = arraste.current;
    if (!a || a.id !== e.pointerId) return;
    if (!a.moveu && Math.hypot(e.clientX - a.sx, e.clientY - a.sy) < 6) return; // ainda é um clique
    a.moveu = true;
    setPos(prender({ x: e.clientX - a.dx, y: e.clientY - a.dy }));
  };
  const up = (e) => {
    const a = arraste.current;
    arraste.current = null;
    if (!a || a.id !== e.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (a.moveu) setPos((p) => { salvar(p); return p; });
    else onClick();
  };
  const padrao = variant === "mobile"
    ? "bottom-28 left-[max(1rem,calc(50%-14rem+1rem))]"
    : cn("bottom-36 right-4 md:right-6", pathname.startsWith("/app/delivery") ? "md:bottom-24" : "md:bottom-6");
  return (
    <button type="button" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => { arraste.current = null; }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      aria-label="Abrir assistente de ajuda (arraste para mudar de lugar)" title="Ajuda — clique para abrir, segure e arraste para mover"
      style={pos ? { left: pos.x, top: pos.y, touchAction: "none" } : { touchAction: "none" }}
      className={cn("fixed z-40 grid h-14 w-14 cursor-grab select-none place-items-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition-transform duration-200 hover:scale-105 active:scale-95 active:cursor-grabbing", !pos && padrao)}
      data-testid="help-fab">
      {fresh && <span className="pointer-events-none absolute inset-0 animate-ping rounded-full bg-primary/40" />}
      <MessageCircleQuestion className="pointer-events-none relative h-6 w-6" />
    </button>
  );
}

export function HelpAssistant({ variant = "app" }) {
  const { session } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [starters, setStarters] = useState([]);
  const [ia, setIa] = useState(false);
  const [seen, setSeen] = useState(() => sessionStorage.getItem("help_seen") === "1");
  const [chat, setChat] = useHelpChat(session.user.id);
  useEffect(() => {
    if (open) api.get("/help/suggestions", { params: { path: pathname } }).then((r) => { setStarters(r.data.items); setIa(!!r.data.ia); }).catch(() => setStarters([]));
  }, [open, pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const show = () => { setOpen(true); setSeen(true); sessionStorage.setItem("help_seen", "1"); };
  const ask = async (text, articleId) => {
    const q = text.trim();
    if (!q || busy) return;
    setChat((c) => ({ ...c, messages: [...c.messages, { id: newId(), role: "user", text: q }] }));
    setBusy(true);
    try {
      // últimas mensagens da conversa, para o assistente entender perguntas de continuação ("e depois?", "e no celular?")
      const history = chat.messages.slice(-6).map((m) => ({ role: m.role, text: m.role === "user" ? m.text : (m.text || m.article?.title || "") }))
        .filter((m) => m.text);
      const [{ data }] = await Promise.all([api.post("/help/ask", { question: q, context: chat.context, article_id: articleId || null, path: pathname, history }), wait(300)]);
      setChat((c) => ({ context: data.article?.id || data.context || c.context, messages: [...c.messages, { id: newId(), role: "bot", ...data }] }));
    } catch {
      setChat((c) => ({ ...c, messages: [...c.messages, { id: newId(), role: "bot", type: "error", text: "Não consegui responder agora. Verifique a internet e tente de novo." }] }));
    }
    setBusy(false);
  };
  const go = (to) => { navigate(to); if (variant === "mobile" || window.innerWidth < 768) setOpen(false); };
  const tour = variant === "app" ? () => { setOpen(false); startTour(); } : null;
  // "Mostrar onde clicar": fecha a ajuda e destaca, tela por tela, o botão de cada passo do artigo
  const guide = variant === "app" ? (a) => { setOpen(false); startGuide(guiaParaTour(a), a.title, a.id); } : null;
  return (
    <>
      {!open && <HelpFab variant={variant} pathname={pathname} fresh={!seen && !chat.messages.length} onClick={show} userId={session.user.id} />}
      <AnimatePresence>
        {open && <HelpPanel variant={variant} chat={chat} busy={busy} starters={starters} firstName={session.user.name.split(" ")[0]}
          onAsk={ask} onReset={() => setChat({ messages: [], context: null })} onClose={() => setOpen(false)} onNavigate={go} onTour={tour} onGuide={guide} ia={ia} />}
      </AnimatePresence>
    </>
  );
}
